import React, { useCallback, useMemo, useState } from 'react';
import { DataPoint, LabelValueList } from 'shared/components/LabelValueList';
import { ToggleCard } from 'shared/components/ToggleCard';
import './style.less';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'shared/store/reducers';
import { createLbbContact, updateLbbCompany, updateLbbContact } from 'shared/api';
import _ from 'lodash';
import { useAppSelector } from 'shared/store';
import { parsePhoneNumber } from 'libphonenumber-js';
import {
  selectFilteredCallTasks,
  setCallTasks,
  setDetailTask,
} from 'shared/store/outbound-slice';
import { CallTask, DialTask } from 'types/outbound';
import { LBBContact, LBBCustomMetadata, LBBOutboundContact } from 'sb-lib/types';
import { ProspectPropertiesModal } from 'browser/components/ProspectPropertiesModal';
import { OrgProspectPropertyGroup } from 'types/user';
import {
  STANDARD_COMPANY_PROPERTY_KEYS,
  STANDARD_CONTACT_PROPERTY_KEYS,
} from 'types/littleblackbook';
import { selectIsAdmin } from 'shared/store/auth-slice';

interface DataPointWithPropertyGroup extends DataPoint {
  propertyGroup: OrgProspectPropertyGroup;
}

export const ContactInfo: React.FC = () => {
  const dispatch = useDispatch();
  const { detailTask: dialTask } = useSelector((state: AppState) => state.outbound);
  const dialTaskContact = dialTask?.contact;
  const callTasks = useAppSelector(selectFilteredCallTasks);
  const isAdmin = useAppSelector(selectIsAdmin);
  const [prospectPropertiesModalVisible, setProspectPropertiesModalVisible] = useState(
    false,
  );
  const orgProspectProperties = useAppSelector(state => state.org.orgProspectProperties);
  const orgProspectPropertiesMeta = useAppSelector(
    state => state.org.orgPropspectPropertiesMeta,
  );

  const getPropertyMetadataByGroupKey = useCallback(
    (propertyGroup: OrgProspectPropertyGroup, propertyKey: string) =>
      orgProspectPropertiesMeta === null
        ? null
        : orgProspectPropertiesMeta?.[propertyGroup]?.[propertyKey],
    [orgProspectPropertiesMeta],
  );

  const getPropertyValueObjectFromContact = (
    contact: LBBOutboundContact,
    propertyGroup: OrgProspectPropertyGroup,
    propertyKey: string,
  ) => {
    let value = null;
    let valueString = '';

    switch (propertyGroup) {
      case 'contact':
        value = STANDARD_CONTACT_PROPERTY_KEYS.includes(propertyKey)
          ? _.get(contact, propertyKey)
          : _.get(contact?.customMetadata, propertyKey);
        break;
      case 'company':
        value = STANDARD_COMPANY_PROPERTY_KEYS.includes(propertyKey)
          ? _.get(contact?.company, propertyKey)
          : _.get(contact?.company?.customMetadata, propertyKey);
        break;
      default:
        value = 'Unknown';
        break;
    }

    // Format the phone number
    if (propertyKey === 'phone') {
      try {
        valueString = parsePhoneNumber(value)?.formatNational();
      } catch (e) {
        valueString = '';
      }
    }

    try {
      valueString = value ? String(value) : '';
    } catch (e) {
      valueString = '';
    }
    return {
      value,
      valueString,
    };
  };

  const currentUserProspectPropertyDataPoints: DataPointWithPropertyGroup[] = useMemo(
    () =>
      orgProspectPropertiesMeta === null || !dialTaskContact
        ? []
        : orgProspectProperties.map(orgProspectProperty => {
            const propertyMeta = getPropertyMetadataByGroupKey(
              orgProspectProperty.propertyGroup,
              orgProspectProperty.propertyKey,
            );

            const propertyValue = getPropertyValueObjectFromContact(
              dialTaskContact,
              orgProspectProperty.propertyGroup,
              orgProspectProperty.propertyKey,
            );

            return {
              label: orgProspectProperty.label,
              value: propertyValue.value,
              valueString: propertyValue.valueString,
              key: orgProspectProperty.propertyKey,
              editable: true,
              propertyGroup: orgProspectProperty.propertyGroup,
              type: propertyMeta?.type,
            };
          }),
    [
      dialTaskContact,
      getPropertyMetadataByGroupKey,
      orgProspectProperties,
      orgProspectPropertiesMeta,
    ],
  );

  const getEntityUpdateObjectWithOptionalCustomMetadata = (
    newProperties: DataPoint[],
    standardPropertyKeys: string[],
  ): Partial<LBBContact> | Partial<LBBContact> => {
    // check if some of the updated properties need to be stored in customMetadata
    const hasCustomProperties = newProperties
      .map(prop => (prop.key ? !standardPropertyKeys.includes(prop.key) : false))
      .reduce((acc, cur) => acc || cur, false);

    // construct an object with property values set in appropriate location
    return newProperties.reduce(
      (acc, cur) => {
        if (cur.key && cur.value) {
          // Everything except standard properties to be stored in custom metadata
          _.set(
            acc,
            standardPropertyKeys.includes(cur.key)
              ? cur.key
              : `customMetadata.${cur.key}`,
            cur.value,
          );
        }
        return acc;
      },
      hasCustomProperties ? { customMetadata: {} as LBBCustomMetadata } : {},
    );
  };

  const handleUpdateProspect = (updatedEntries: DataPointWithPropertyGroup[]) => {
    // split properties into contact/company
    const companyProperties = updatedEntries.filter(
      entry => entry.propertyGroup === 'company',
    );
    const contactProperties = updatedEntries.filter(
      entry => entry.propertyGroup === 'contact',
    );

    const contactUpdate = getEntityUpdateObjectWithOptionalCustomMetadata(
      contactProperties,
      STANDARD_CONTACT_PROPERTY_KEYS,
    );

    const companyUpdate = getEntityUpdateObjectWithOptionalCustomMetadata(
      companyProperties,
      STANDARD_COMPANY_PROPERTY_KEYS,
    );

    if (!dialTaskContact?.id) {
      const params: { [key: string]: string | Record<string, string> } = {};

      if (contactUpdate.email) {
        params['email'] = contactUpdate.email as string;
      } else if (contactUpdate.name) {
        params['name'] = contactUpdate.name as string;
      } else if (contactUpdate.phone) {
        params['phone'] = contactUpdate.phone as string;
      } else if ((contactUpdate.customMetadata as Record<string, string>)?.title) {
        params.customMetadata = {};
        params.customMetadata.title = (contactUpdate.customMetadata as Record<
          string,
          string
        >)?.title as string;
      }

      createLbbContact(params);
    } else {
      if (contactUpdate.customMetadata) {
        contactUpdate.customMetadata = {
          ...dialTaskContact.customMetadata,
          ...(contactUpdate.customMetadata as LBBCustomMetadata),
        };
      }
      // Only update if there is actually any data
      if (Object.keys(contactUpdate).length > 0) {
        updateLbbContact(dialTaskContact.id, contactUpdate);
      }

      if (dialTaskContact.company?.id && Object.keys(companyUpdate).length > 0) {
        updateLbbCompany(dialTaskContact.company?.id, companyUpdate);
      }
    }

    const finalContact = {
      ...dialTaskContact,
      ...contactUpdate,
      company: {
        ...dialTaskContact?.company,
        ...companyUpdate,
      },
    } as LBBOutboundContact;

    // Update the state to reflect the changes in other parts of the UI
    dispatch(
      setDetailTask({
        ...dialTask,
        contact: finalContact,
      } as DialTask),
    );

    dispatch(
      setCallTasks(
        callTasks.map((callTask: CallTask) => {
          if (callTask.contact.id === finalContact.id) {
            return {
              ...callTask,
              contact: {
                ...callTask?.contact,
                ...finalContact,
              },
            };
          }

          return callTask;
        }),
      ),
    );
  };

  const header = (
    <div className="OutboundContactInfo__Header">
      <span>About</span>
      {isAdmin && (
        <span
          className="OutboundContactInfo__ManageProspectPropertiesToggle"
          onClick={e => {
            e.stopPropagation();
            setProspectPropertiesModalVisible(true);
          }}
        >
          Manage
        </span>
      )}
    </div>
  );
  const ulsKey = 'OutboundContactDetailInfo';

  const [contactDataPoints, companyDataPoints] = useMemo(
    () =>
      currentUserProspectPropertyDataPoints.reduce(
        (acc, cur) => {
          if (cur.propertyGroup === 'contact') {
            acc[0].push(cur);
          } else {
            acc[1].push(cur);
          }
          return acc;
        },
        [[], []] as [DataPointWithPropertyGroup[], DataPointWithPropertyGroup[]],
      ),
    [currentUserProspectPropertyDataPoints],
  );

  return (
    <div className="OutboundContactInfo">
      <ToggleCard ulsKey={ulsKey} header={header}>
        <div className="OutboundContactInfo__Content">
          <LabelValueList
            data={contactDataPoints}
            onDataChange={(updatedDataPoints: DataPoint[]) => {
              handleUpdateProspect(updatedDataPoints as DataPointWithPropertyGroup[]);
            }}
          />
        </div>

        {!!companyDataPoints.length && (
          <>
            <div className="OutboundContactInfo__contentHeader">Company</div>
            <div className="OutboundContactInfo__Content">
              <LabelValueList
                data={companyDataPoints}
                onDataChange={(updatedDataPoints: DataPoint[]) => {
                  handleUpdateProspect(updatedDataPoints as DataPointWithPropertyGroup[]);
                }}
              />
            </div>
          </>
        )}
        {prospectPropertiesModalVisible && (
          <ProspectPropertiesModal
            onCancel={() => setProspectPropertiesModalVisible(false)}
            onOk={() => setProspectPropertiesModalVisible(false)}
          />
        )}
      </ToggleCard>
    </div>
  );
};
